import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import type { CheckboxState } from '../../models/checkbox.models';
import { ButtonComponent } from '../button/button.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { HeadingComponent } from '../heading/heading.component';
import { IconButtonNewComponent } from '../icon-button-new/icon-button-new.component';
import { IconComponent } from '../icon/icon.component';
import { featureFlagDefinition } from './feature-flag-definition.consts';
import {
  isFeatureFlagKey,
  type FeatureFlag,
  type FeatureFlagItem,
} from './feature-flag.models';
import { getIsFeatureFlagEnabled } from './get-is-feature-flag-enabled.helper';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-feature-flag-manager',
  templateUrl: './feature-flag-manager.component.html',
  imports: [
    ButtonComponent,
    CheckboxComponent,
    HeadingComponent,
    IconButtonNewComponent,
    IconComponent,
  ],
  host: {
    class: 'tw-fixed tw-bottom-[160px] tw-right-4 tw-z-[9999]',
    '(document:click)': 'onDocumentClick($event)',
  },
})
export class FeatureFlagManagerComponent {
  readonly #elementRef = inject(ElementRef);
  readonly #sessionStorage = inject(SessionStorageService);

  readonly isOpen = signal(false);

  readonly flagItems = signal<FeatureFlagItem[]>(this.#getFlagItems());

  onFeatureFlagManagerTriggerClick(): void {
    this.isOpen.update((open) => !open);
  }

  onDocumentClick(event: MouseEvent): void {
    const host = this.#elementRef.nativeElement as HTMLElement;
    const target = event.target;
    if (!(target instanceof Node) || !host.contains(target)) {
      this.#closeWithoutSaving();
    }
  }

  onCheckboxStateChange(flagId: keyof FeatureFlag, state: CheckboxState): void {
    this.flagItems.update((items) =>
      items.map((item) =>
        item.id === flagId ? { ...item, checkboxState: state } : item,
      ),
    );
  }

  onSaveAndReload(): void {
    for (const flag of this.flagItems()) {
      this.#sessionStorage.setItem(flag.id, flag.checkboxState === 'checked');
    }

    globalThis.location.reload();
  }

  onResetToDefaults(): void {
    this.flagItems.set(
      this.#getFlagItems().map((flagItem) => ({
        ...flagItem,
        checkboxState:
          featureFlagDefinition[flagItem.id].defaultState ?
            'checked'
          : 'unchecked',
      })),
    );
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  #getFlagItems(): FeatureFlagItem[] {
    return Object.keys(featureFlagDefinition)
      .filter((key) => isFeatureFlagKey(key))
      .filter((id) => featureFlagDefinition[id].showInPanel)
      .map(
        (id) =>
          ({
            id,
            checkboxState:
              getIsFeatureFlagEnabled(id) ? 'checked' : 'unchecked',
            ...featureFlagDefinition[id],
          }) satisfies FeatureFlagItem,
      );
  }

  #closeWithoutSaving(): void {
    this.flagItems.set(this.#getFlagItems());

    this.isOpen.set(false);
  }
}
